/**
 * OSM Directions Service (backend-powered)
 */

export interface OSMRouteResult {
    polyline: string;
    distance: { text: string; value: number };
    duration: { text: string; value: number };
    waypointOrder: number[];
    provider: string;
}

const routeCache = new Map<string, OSMRouteResult>();

/**
 * Generate route using OSM via backend API
 */
export async function generateOSMRoute(
    coordinates: [number, number][],
    country: string = "NL",
    optimize: boolean = false,
    fixStartEnd?: boolean
): Promise<OSMRouteResult | null> {
    const fingerprint = JSON.stringify({ coordinates, country, optimize, fixStartEnd });
    if (routeCache.has(fingerprint)) {
        return routeCache.get(fingerprint) || null;
    }

    try {
        const res = await fetch("/api/osm/route", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ coordinates, country, optimize, fixStartEnd })
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const errorMsg = err.details || err.error || err.message || "Failed to calculate OSM route";
            console.error(`[OSM Route] Server returned ${res.status}:`, errorMsg);
            throw new Error(errorMsg);
        }

        const data = await res.json();

        // Format to match RouteResult interface
        const result: OSMRouteResult = {
            polyline: data.polyline,
            distance: {
                text: data.distance_meters > 1000
                    ? `${(data.distance_meters / 1000).toFixed(1)} km`
                    : `${Math.round(data.distance_meters)} m`,
                value: data.distance_meters
            },
            duration: {
                text: data.duration_seconds > 3600
                    ? `${Math.floor(data.duration_seconds / 3600)} u ${Math.round((data.duration_seconds % 3600) / 60)} min`
                    : `${Math.round(data.duration_seconds / 60)} min`,
                value: data.duration_seconds
            },
            waypointOrder: data.waypoint_order || [],
            provider: data.provider
        };

        routeCache.set(fingerprint, result);
        return result;
    } catch (err) {
        console.error("OSM route calculation failed:", err);
        return null;
    }
}
